import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@mdvip/shared/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@mdvip/shared/components/ui/sheet';
import { useIsMobile } from '@mdvip/shared/hooks/useIsMobile';
import { cn } from '@mdvip/shared/lib/utils';
import * as React from 'react';

interface ActionSheetContextValue {
  isMobile: boolean;
  close: () => void;
}

const ActionSheetContext = React.createContext<ActionSheetContextValue>({
  isMobile: false,
  close: () => {},
});

/**
 * ActionSheet — responsive action menu. Renders as a DropdownMenu on desktop
 * (sm+) and a bottom Sheet on mobile. Compose with ActionSheetTrigger,
 * ActionSheetContent, and ActionSheetItem. The trigger, content, and items
 * automatically adapt to the active layout — no conditional rendering needed
 * at the call site.
 */
function ActionSheet({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const close = React.useCallback(() => setOpen(false), []);

  if (isMobile) {
    return (
      <ActionSheetContext.Provider value={{ isMobile: true, close }}>
        <Sheet open={open} onOpenChange={setOpen}>
          {children}
        </Sheet>
      </ActionSheetContext.Provider>
    );
  }

  return (
    <ActionSheetContext.Provider value={{ isMobile: false, close: () => {} }}>
      <DropdownMenu>{children}</DropdownMenu>
    </ActionSheetContext.Provider>
  );
}

interface ActionSheetTriggerProps {
  render?: React.ReactElement;
  children?: React.ReactNode;
  className?: string;
}

/** Renders as SheetTrigger on mobile and DropdownMenuTrigger on desktop. */
function ActionSheetTrigger({ render, children, className }: ActionSheetTriggerProps) {
  const { isMobile } = React.useContext(ActionSheetContext);

  if (isMobile) {
    return (
      <SheetTrigger render={render} className={className}>
        {children}
      </SheetTrigger>
    );
  }

  return (
    <DropdownMenuTrigger render={render} className={className}>
      {children}
    </DropdownMenuTrigger>
  );
}

interface ActionSheetContentProps {
  /** Sheet header title shown on mobile. Also serves as the accessible dialog label. */
  title?: string;
  /** Alignment of the dropdown on desktop. Defaults to "end". */
  align?: 'start' | 'center' | 'end';
  /** Extra classes applied to the dropdown on desktop (e.g. min-w override). */
  className?: string;
  children?: React.ReactNode;
  /** Informational footnote rendered below items on mobile only (e.g. "Your default method can't be removed."). */
  note?: string;
}

/** Renders as SheetContent (bottom) on mobile and DropdownMenuContent on desktop. */
function ActionSheetContent({ title, align = 'end', className, children, note }: ActionSheetContentProps) {
  const { isMobile } = React.useContext(ActionSheetContext);

  if (isMobile) {
    return (
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="px-0 pt-0 [padding-bottom:max(0.5rem,env(safe-area-inset-bottom))]"
      >
        <SheetHeader className="px-4 pb-2 pt-4">
          <SheetTitle>{title ?? 'Actions'}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col">{children}</div>
        {note && <p className="px-3 pb-1 pt-1.5 text-xs text-muted-foreground">{note}</p>}
      </SheetContent>
    );
  }

  return (
    <DropdownMenuContent align={align} className={cn('min-w-[180px]', className)}>
      {children}
    </DropdownMenuContent>
  );
}

export interface ActionSheetItemProps {
  /** Leading icon node — typically an <Icon>. */
  icon?: React.ReactNode;
  children?: React.ReactNode;
  onSelect?: () => void;
  /** Applies destructive (red) treatment. */
  destructive?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * ActionSheetItem — a single action row. On mobile: a full-width button with a
 * 44px min tap target. On desktop: a DropdownMenuItem. The menu/sheet closes
 * automatically after selection.
 */
function ActionSheetItem({
  icon,
  children,
  onSelect,
  destructive = false,
  disabled = false,
  className,
}: ActionSheetItemProps) {
  const { isMobile, close } = React.useContext(ActionSheetContext);

  if (isMobile) {
    return (
      <button
        type="button"
        onClick={() => {
          onSelect?.();
          close();
        }}
        disabled={disabled}
        className={cn(
          'flex min-h-11 w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
          destructive ? 'text-destructive-foreground' : 'text-foreground',
          className,
        )}
      >
        {icon && <span className="flex w-5 shrink-0 items-center justify-center">{icon}</span>}
        <span className="min-w-0 flex-1 truncate">{children}</span>
      </button>
    );
  }

  return (
    <DropdownMenuItem
      variant={destructive ? 'destructive' : 'default'}
      onClick={onSelect}
      disabled={disabled}
      className={className}
    >
      {icon}
      {children}
    </DropdownMenuItem>
  );
}

export { ActionSheet, ActionSheetContent, ActionSheetItem, ActionSheetTrigger };
